import type { SearchHousesSplitMapRepository } from './interfaces/SearchHousesSplitMapRepository'
import type { SearchHousesSplitMapPresentation } from './interfaces/SearchHousesSplitMapPresentation'
import type { SearchRepository } from '../SearchHouses/SearchHousesWithDates/interfaces/SearchHousesWithDatesRepository'

import { Search } from '../../domain'
import { PRICES, chunkArray } from '../../domain/helpers'

import type { GetSearchParamsType } from '../SearchHouses/types'
import type { SearchType } from '../../../../services/algolia'
import type {
  HousePeriodsIdsResponseData,
  Locale,
  Mode,
  QueryModelType,
  SearchHouse,
  SearchHouseCoordinates,
  SupportedCurrencies,
} from '../../../../types'

export default class SearchHousesSplitMapUseCase extends Search {
  constructor(private repository: SearchHousesSplitMapRepository) {
    super()
  }

  async getSearchHousesInfoWithoutDate(
    input: GetSearchParamsType,
    deviceMode: 'mobile' | 'web',
    mode: Mode,
    repository: SearchRepository,
    locale?: Locale,
    algoliaFilters?: string,
  ) {
    const searchParams = this.getSearchParams(input, deviceMode, mode, locale)

    const [list, map] = await Promise.all([
      this.getSearchResultByPage({
        ...searchParams,
        algoliaFilters,
        hitsPerPage: input.hitsPerPage,
        mode,
        page: input.period ? 0 : input.page,
        repository,
      }),
      input.initializelist
        ? this.getBrowseResponse<SearchHouseCoordinates>({
            ...searchParams,
            attributesToRetrieve: ['id', '_geoloc'],
            repository,
            mode,
            algoliaFilters,
          })
        : undefined,
    ])

    return {
      facets: list.facets,
      hits: list.hits,
      hitsCoordinates: map,
      nbHits: list.nbHits,
      nbPages: list.nbPages,
      queryID: list.queryID,
    }
  }

  async getSearchHousesInfoWithDate(
    input: GetSearchParamsType,
    deviceMode: 'mobile' | 'web',
    mode: Mode,
    repository: SearchRepository,
    locale?: Locale,
    algoliaFilters?: string,
  ) {
    const searchParams = this.getSearchParams(input, deviceMode, mode, locale)

    const resHits = await this.getBrowseResponse<
      Pick<SearchHouse, 'id' | '_geoloc'>
    >({
      ...searchParams,
      attributesToRetrieve: ['id', '_geoloc'],
      repository,
      mode,
      algoliaFilters,
    })

    const validIds = input.housesPeriodIds?.validHousePeriodsIds || []
    const invalidIds = input.housesPeriodIds?.invalidHousePeriodsIds || []
    const ids = validIds.concat(invalidIds)
    const idsFiltered = ids.filter((id) =>
      resHits.map((p) => p.id).includes(id),
    )
    const hitsCoordinatesValid = resHits.filter((h) => ids.includes(h.id))

    const housesIdsQuery =
      idsFiltered.length > 0
        ? `AND (${idsFiltered.map((num) => `id=${num.toString()}`).join(' OR ')})`
        : ''

    const chunks = chunkArray(idsFiltered, input.hitsPerPage || 20)

    const res = await Promise.all([
      // get x results
      chunks[input.page]?.length
        ? repository.algolia.getObjects<SearchHouse>(
            searchParams.replica,
            chunks[input.page],
          )
        : { results: [] as SearchHouse[] },
      // get facets
      input.initializelist
        ? this.getSearchResponse({
            ...searchParams,
            housesIds: housesIdsQuery,
            repository,
            mode,
            algoliaFilters,
          })
        : undefined,
      // get periods
      chunks[input.page]?.length
        ? this.fetchHousePeriodsResponseSplitMap({
            deviceMode,
            currency: this.currency,
            houseIds: chunks[input.page].map(String),
            maxBudget: input.maxPrice,
            minBudget: input.minPrice,
            page: input.page,
            period: input.period,
            repository,
            sortBy: input.replicaValue,
          })
        : undefined,
    ])

    const { results } = res[0]
    this.facetsFilters = res[1]

    return {
      facets: this.facetsFilters,
      hits: this.getNewHits(results),
      hitsCoordinates: hitsCoordinatesValid,
      nbHits: hitsCoordinatesValid.length,
      nbPages: chunks.length,
      queryID: undefined,
    }
  }

  async execute(
    presenter: SearchHousesSplitMapPresentation,
    {
      aroundLatLng,
      aroundRadius,
      bathrooms = '',
      bedrooms = '',
      capacity = '',
      destinationIds = [],
      housesIds = [],
      facetFilters = new Map(),
      initializelist = false,
      itemsPerPage = 15,
      page = 0,
      prices = [PRICES.MIN_PRICE_CHOICE, PRICES.MAX_PRICE_CHOICE],
      period,
      query = '',
      replica = 'recommended',
      mode = 'client',
      algoliaFilters = '',
      insideBoundingBox,
      housesPeriodIds,
    }: QueryModelType,
    deviceMode: 'mobile' | 'web',
    locale: Locale,
    currency: SupportedCurrencies | null,
    algoliaIds: SearchType,
    requestInfo: { lastId: number },
  ) {
    const currentRequestId = requestInfo.lastId

    try {
      const { getHousePeriodsIds, getHousePeriods, algolia } = this.repository
      const repository = {
        getHousePeriodsIds,
        getHousePeriods,
        algolia: algolia(algoliaIds),
      } as SearchRepository

      this.setCurrentPage(page)
      this.setHitsPerPage(itemsPerPage)
      this.setCurrency(currency || undefined)
      this.setLocale(locale)

      const input = {
        aroundLatLng,
        aroundRadius,
        bathrooms,
        bedrooms,
        capacity,
        currency: this.currency,
        destinationIds,
        hitsPerPage: itemsPerPage,
        housesIds,
        facetFilters,
        initializelist,
        maxPrice: prices[1] || PRICES.MAX_PRICE_CHOICE,
        minPrice: prices[0] || PRICES.MIN_PRICE_CHOICE,
        page,
        period,
        query,
        replicaValue: replica,
        mode,
        deviceMode,
        algoliaFilters,
        insideBoundingBox,
      }

      const haveHouseIdsPeriods = Boolean(
        housesPeriodIds?.invalidHousePeriodsIds.length ||
          housesPeriodIds?.validHousePeriodsIds.length,
      )

      let housesPeriodIdsNew: HousePeriodsIdsResponseData | undefined
      if (period && !haveHouseIdsPeriods) {
        const housePeriodIdsResponse = await this.getHouseIdsPeriodResponse({
          currency: this.currency,
          destinationIds,
          deviceMode,
          housesIds,
          maxBudget: prices[1] as number,
          minBudget: prices[0] as number,
          period,
          repository,
          sortBy: replica,
        })
        housesPeriodIdsNew = housePeriodIdsResponse?.data
        if (housesPeriodIdsNew)
          presenter.displayHousesPeriodIds(housesPeriodIdsNew)
      }

      const { hits, hitsCoordinates, facets, nbHits, nbPages, queryID } =
        input.period
          ? // case with date
            await this.getSearchHousesInfoWithDate(
              {
                ...input,
                housesPeriodIds: haveHouseIdsPeriods
                  ? housesPeriodIds
                  : housesPeriodIdsNew,
              },
              deviceMode,
              mode,
              repository,
              locale,
              algoliaFilters,
            )
          : // case without date
            await this.getSearchHousesInfoWithoutDate(
              { ...input },
              deviceMode,
              mode,
              repository,
              locale,
              algoliaFilters,
            )

      // Algolia cannot permit to abort request
      // So need to soft cancel,  fill presenter only is the last request
      if (currentRequestId !== requestInfo.lastId) return

      if (initializelist) {
        this.initializeList()
        this.setCurrentPage(page)

        this.facetsFilters = facets

        this.setNbHits(nbHits || 0)
        this.setNbPages(nbPages || 0)

        presenter.displayFacets(this.facetsFilters)
        presenter.displayNbPages(this.nbPages)
        presenter.displayNbHouses(this.nbHits)
        if (typeof hitsCoordinates !== 'undefined') {
          const currentHitsMap = this.formatCoordinatesMap(hitsCoordinates)
          presenter.displaySearchHousesSplitMapResult(currentHitsMap)
          presenter.displayCoordinatesBoundaries(this.coordinatesBoundaries)
        }
      }

      this.currentHits = [...this.currentHits, ...hits]

      presenter.displaySearchCurrentPage(this.currentPage)
      presenter.displaySearchHousesSplitListResult(this.currentHits)
      presenter.displayAlgoliaQueryId(queryID)
    } catch (err) {
      console.error('SearchHousesSplitMapUseCase', err)
    }
  }
}
